import { useTranslation } from 'react-i18next';
import { Link, To } from 'react-router-dom';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { withIcon } from '../../../core/components/Icon.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { useGetFieldLabel } from '../../llmExtractorFields.ts';
import { FIELD_COLOR_CLASS } from '../PromptEditor.tsx';
import { getRuleScopeTitle, getRuleStatus, getRuleTitle, Rule } from './rules.ts';

const ChevronRightIcon = withIcon(faChevronRight);

interface RuleRowProps {
    rule: Rule;
    to: To;
}

// a closed rule: who it's for (when it has a name), the instructions in one line, the output fields as chips
const RuleRow = ({ rule, to }: RuleRowProps) => {
    const { t } = useTranslation('config');
    const getFieldLabel = useGetFieldLabel();
    const status = getRuleStatus(rule, t);
    return (
        <Link
            to={to}
            className="flex w-full cursor-pointer items-start gap-3 rounded-lg border border-solid border-line-weak bg-transparent px-4 py-3 text-left transition-colors hover:bg-surface-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong"
        >
            <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                <span className="flex items-baseline gap-3">
                    <span className="flex-1 text-sm font-medium text-content-strong">{getRuleTitle(rule, t)}</span>
                    {status && <span className="text-xs text-content-medium">{status}</span>}
                </span>
                <span className="flex flex-col gap-0.5 text-xs text-content-medium">
                    <span className="line-clamp-1">
                        {rule.name && `${getRuleScopeTitle(rule, t)} · `}
                        {rule.prompt || t('configPage.fineTuning.noInstructions')}
                    </span>
                    {!!rule.fields?.length && (
                        <span className="flex flex-wrap items-center gap-1 pt-0.5">
                            <span>{t('configPage.fineTuning.outputFields')}</span>
                            {rule.fields.map((name) => (
                                <span
                                    key={name}
                                    className={classnames(
                                        'rounded px-1 py-0.5 text-xs font-medium',
                                        FIELD_COLOR_CLASS.output
                                    )}
                                >
                                    {getFieldLabel(name)}
                                </span>
                            ))}
                        </span>
                    )}
                </span>
            </span>
            <ChevronRightIcon className="flex-none self-center text-content-medium" />
        </Link>
    );
};

export default RuleRow;
